/**
 * The in-person join sequence: `provider-join` (which starts the capture on the server), then the local
 * audio-only media. Kept apart from the React hook so the failure handling can be unit-tested.
 */

import { pickDefaultDevice } from '@/lib/telehealth/chime/chimeDevices';

export const IN_PERSON_MICROPHONE_ERROR_TEXT = 'Could not use the microphone. Check it is connected, then try again.';

export type InPersonMicrophoneMedia = Pick<MediaDevices, 'getUserMedia' | 'enumerateDevices'>;

/**
 * Asks for the microphone first (until then browsers list devices with no id or label), then lists the
 * microphones and picks one: `previous` while it still exists, else the system default, else the first.
 * Throws when there is none, so a start fails before `provider-join` instead of recording silence.
 */
export async function acquireInPersonMicrophone(
  media: InPersonMicrophoneMedia | undefined,
  previous: string | undefined,
): Promise<{ devices: MediaDeviceInfo[]; deviceId: string }> {
  let stream: MediaStream | undefined;
  try {
    stream = await media?.getUserMedia({ audio: true });
  } catch {
    /* denied or no device: the listing below decides */
  }
  try {
    const listed = (await media?.enumerateDevices().catch(() => [])) ?? [];
    const devices = listed.filter((d) => d.kind === 'audioinput');
    const deviceId = pickDefaultDevice(devices, previous);
    if (!deviceId) throw new Error(IN_PERSON_MICROPHONE_ERROR_TEXT);
    return { devices, deviceId };
  } finally {
    // Released only after listing: Safari may expose device ids only while a capture is active.
    stream?.getTracks().forEach((track) => track.stop());
  }
}

export type InPersonJoinResult<TMeeting extends { MeetingId?: string }, TAttendee> = {
  meeting?: TMeeting;
  attendee?: TAttendee;
  /** From the server: true only when this join created the capture pipeline. Absent means unknown. */
  captureStartedByThisJoin?: boolean;
};

export type InPersonJoinDeps<TMeeting extends { MeetingId?: string }, TAttendee> = {
  providerJoin: () => Promise<InPersonJoinResult<TMeeting, TAttendee>>;
  /** Starts the local media; false when the microphone could not be used (or none was chosen). */
  startMedia: (meeting: TMeeting, attendee: TAttendee) => Promise<boolean>;
  /** Called with the meeting id as soon as `provider-join` returns one. */
  onMeeting?: (meetingId: string | undefined) => void;
  /** `end` with `expectedMeetingId`; its errors are swallowed and logged here. */
  end: (meetingId: string | undefined) => Promise<unknown>;
  /**
   * End the session when the local media start fails after `provider-join` succeeded, so the capture
   * stops and the Appointment does not sit at `transcribing`. False on a rejoin, which must not end a
   * running session. Even then it ends only a capture THIS join started (the server's
   * `captureStartedByThisJoin`); if that is false or absent (ownership unknown), another device may own
   * the capture, and ending it would lose that recording, so nothing is ended.
   */
  endOnFailure: boolean;
};

/**
 * Throws when the join cannot complete. A failure of `provider-join` itself throws untouched (no capture
 * started, nothing to end). A failure after it calls `end` best effort first, then rethrows the original
 * error, unless this join did not start the capture (`captureStartedByThisJoin` not true).
 */
export async function runInPersonJoin<TMeeting extends { MeetingId?: string }, TAttendee>(
  deps: InPersonJoinDeps<TMeeting, TAttendee>,
): Promise<void> {
  const result = await deps.providerJoin();
  const meetingId = result.meeting?.MeetingId;
  const ownsCapture = result.captureStartedByThisJoin === true;
  try {
    if (!result.meeting || !result.attendee) throw new Error('The recording session did not start');
    deps.onMeeting?.(meetingId);
    const joined = await deps.startMedia(result.meeting, result.attendee);
    if (!joined) throw new Error(IN_PERSON_MICROPHONE_ERROR_TEXT);
  } catch (e) {
    if (deps.endOnFailure && ownsCapture) {
      try {
        await deps.end(meetingId);
      } catch (endError) {
        console.warn('Could not end the in-person session after a failed start', endError);
      }
    }
    throw e;
  }
}

export type InPersonEndDeps = {
  /** `end` with `expectedMeetingId`; resolves the server's answer. */
  end: () => Promise<{ ended?: boolean }>;
  /** The server ended the session: stop local media, release the wake lock, reset the page. */
  onEnded: () => Promise<void>;
  /** The server ignored the End (the meeting is no longer current): keep everything, tell the provider. */
  onDeclined: () => void;
};

/**
 * End the in-person session. `{ ended: false }` means a newer session on another device replaced this
 * meeting and nothing was ended, so the page's state, media and wake lock stay as they are.
 */
export async function runInPersonEnd(deps: InPersonEndDeps): Promise<'ended' | 'declined'> {
  const result = await deps.end();
  if (result.ended === false) {
    deps.onDeclined();
    return 'declined';
  }
  await deps.onEnded();
  return 'ended';
}
